/* eslint-disable no-nested-ternary */
import { Delete } from '@mui/icons-material';
import { Box, FormControlLabel, Grid, IconButton, Switch, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { Ingredient, Supply } from '../../apiTypes';
import { t } from '../../constants/translations';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { getInitMap } from '../../utils/map';
import useMedia from '../../utils/useMedia';
import AddIngredientButton from '../AddIngredientButton';
import Image from '../Image';
import FormCostField from './FormCostField';
import FormQntInput from './FormQntInput';

export type FormComponentsTableProps = {
  options: Ingredient[];
  form: UseFormReturn<Supply>;
  disablePackingInit?: boolean;
  disabled?: boolean;
};

const FormSupplyComponentsTable = ({
  options,
  form,
  disablePackingInit = false,
  disabled,
}: FormComponentsTableProps) => {
  const { lang } = useLocale();
  const { isMobile } = useMedia();
  const { stores, productsMap } = useCafe();

  const [disablePacking, setDisablePacking] = useState(disablePackingInit);

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'components',
    keyName: 'key',
  });

  const filteredOptions = options.filter((o) => !fields.some((c) => c.id === o.id));

  const optionsMap = useMemo(
    () => options.reduce((acc, cur) => ({ ...acc, [cur.id]: cur }), getInitMap<Ingredient>()),
    [options]
  );

  const components = form.watch('components');

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        {fields.length > 0 && (
          <FormControlLabel
            control={
              <Switch size="small" checked={!disablePacking} onChange={(e) => setDisablePacking(!e.target.checked)} />
            }
            label={t.display_packaging[lang]}
          />
        )}
      </Box>

      {!isMobile && fields.length > 0 && (
        <Grid container spacing={1}>
          <Grid item md={4}>
            <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
              {t.component[lang]}
            </Typography>
          </Grid>
          <Grid item md={3.5}>
            <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
              {t.quantity[lang]}
            </Typography>
          </Grid>
          <Grid item md={2}>
            <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
              {t.price[lang]}
            </Typography>
          </Grid>
          <Grid item md={2}>
            <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
              {t.total[lang]}
            </Typography>
          </Grid>
        </Grid>
      )}
      {fields.map((field, index) => (
        <Grid
          key={field.key}
          container
          alignItems="center"
          mb={{ xs: 2, md: 0 }}
          sx={{ p: 0.5, borderRadius: 1, ':hover': { bgcolor: (t) => t.palette.background.default } }}
        >
          <Grid item xs={10} md={4}>
            <Box gap={1} sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
              <Image src={optionsMap[field.id]?.imageUrl} />
              <Box>
                <Typography sx={{ lineHeight: 0.8 }}>{optionsMap[field.id]?.name}</Typography>
              </Box>
            </Box>
          </Grid>

          {isMobile && (
            <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton
                sx={{ width: 32, height: 32, ml: 1, flexShrink: 0 }}
                size="small"
                onClick={() => remove(index)}
              >
                <Delete />
              </IconButton>
            </Grid>
          )}

          <Grid item xs={12} md={3.5} sx={{ pr: { xs: 0, md: 2 }, my: { xs: 1, md: 0 } }}>
            <FormQntInput
              ingredient={{ unit: optionsMap[field.id]?.unit || 'g', qpp: optionsMap[field.id]?.qpp }}
              name={`components.${index}.qnt`}
              disablePacking={disablePacking}
              disabled={disabled}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormCostField
              name={`components.${index}.price`}
              qnt={components[index]?.qnt || 0}
              ingredient={optionsMap[field.id]}
              disabled={disabled}
            />
          </Grid>

          {!isMobile && (
            <Grid item md={0.5}>
              {!disabled && (
                <IconButton
                  sx={{ width: 32, height: 32, ml: 1, flexShrink: 0 }}
                  size="small"
                  onClick={() => remove(index)}
                >
                  <Delete />
                </IconButton>
              )}
            </Grid>
          )}
        </Grid>
      ))}

      {!disabled && (
        <Box sx={{ maxWidth: 260, my: 0.5 }}>
          <AddIngredientButton
            options={filteredOptions}
            onAdd={(value) =>
              append({
                id: value.id,
                store: stores[0]?.id || '',
                qnt: 0,
                price: productsMap[value.id]?.price || null,
              })
            }
          />
        </Box>
      )}
    </Box>
  );
};

export default FormSupplyComponentsTable;
